/**
 * The two writes the Materiały block makes, and the one key set they drop.
 *
 * **Which endpoint depends on whose exercise it is, and that is not a preference.** An overlay is
 * something a trainer puts on somebody ELSE's movement; on their own it is refused with 403 by
 * design, and the links travel on the row itself. `model/materialsStatus` decides which of the two
 * a given movement takes and hands over `route`; this module only makes the call.
 *
 * · **`overlay`** — `PUT /exercise/{id}/overlay/links`. Creates or replaces the trainer's version of
 *   a library movement's materials. `DELETE /exercise/{id}/overlay` is the way back.
 * · **`exercise`** — `PATCH /exercise/{id}`, with `links` and nothing else. A partial write: every
 *   field left out is left alone, so an editor that sends only the materials cannot flatten a name
 *   or a muscle list it never showed.
 *
 * ── `lang` on the PUT, and none on the PATCH ────────────────────────────────────────────────
 * Both answer with the whole localised exercise, and the PUT takes `lang` for it — a blank one
 * resolves to English rather than erroring, so omitting it would answer a Polish trainer's save
 * with an English name. `PATCH /exercise/{id}` carries no such parameter on the contract, so
 * nothing is invented for it: the response is thrown away and the catalogue is re-read instead.
 *
 * ── ONE invalidation, and it is the catalogue's root ────────────────────────────────────────
 * `exerciseLibraryRoot` is the prefix under which the catalogue, `similar` and `overlay` all hang,
 * so dropping it drops the row the table draws, the count in the header's `Z materiałami` figure
 * and the overlay reading the block's own sentence is composed from — the three things a materials
 * write changes. An invalidation per key would be three lists to remember together, and the overlay
 * is precisely the one that gets forgotten.
 *
 * ── A 403 from the overlay path is an ANSWER, and it is worded ──────────────────────────────
 * The trainer reached the overlay route on a movement the backend considers theirs — a stale
 * ownership reading, or a movement duplicated in another tab. It is not a crash and it is not
 * "spróbuj ponownie": mobile already has the sentence that says what to do instead
 * (`exerciseLibrary.links.notAllowedError`), and `materialsRefusalKey` is what picks it.
 */

import type { QueryClient } from '@tanstack/react-query';

import { exerciseLibraryRoot } from '~/entities/exercise-library';
import { ApiError } from '~/shared/api/mutator';
import {
  deleteExerciseIdOverlay,
  patchExerciseId,
  putExerciseIdOverlayLinks,
} from '~/shared/api/generated/endpoints/exercise/exercise';
import type { ContentLanguage } from '~/shared/i18n/contentLanguage';

/** One material, as the wire takes it. Both endpoints spell it the same way. */
export interface MaterialLinkWrite {
  readonly url: string;
  readonly label: string;
}

export interface SaveMaterialsCommand {
  readonly exerciseId: number;
  /** `model/materialsStatus`'s answer. Never guessed at the call site. */
  readonly route: 'overlay' | 'exercise';
  readonly links: readonly MaterialLinkWrite[];
  readonly lang: ContentLanguage;
}

/** Write the materials, through whichever route this movement's ownership decided. */
export async function saveExerciseMaterials(command: SaveMaterialsCommand): Promise<void> {
  const links = command.links.map((link) => ({ url: link.url, label: link.label }));

  if (command.route === 'exercise') {
    await patchExerciseId(command.exerciseId, { links });
    return;
  }
  await putExerciseIdOverlayLinks(command.exerciseId, { links }, { lang: command.lang });
}

/** Drop the overlay and read the library's own links again. Only ever legal on `libraryCustomised`. */
export async function revertExerciseMaterials(exerciseId: number): Promise<void> {
  await deleteExerciseIdOverlay(exerciseId);
}

/**
 * Drop everything that describes this catalogue.
 *
 * `await`ed by the caller's `onSuccess`, so the mutation stays pending until the row the block is
 * about to redraw has actually arrived — otherwise the editor closes onto the links it just
 * replaced and they change under the trainer a beat later.
 */
export function invalidateExerciseMaterials(client: QueryClient): Promise<void> {
  return client.invalidateQueries({ queryKey: exerciseLibraryRoot });
}

/**
 * Which sentence a failed write gets, as a KEY.
 *
 * Two outcomes and no third: the designed refusal, and everything else. A wider table would be
 * inventing distinctions the backend does not draw at this endpoint.
 */
export function materialsRefusalKey(error: unknown): string {
  if (error instanceof ApiError && error.status === 403) {
    return 'exerciseLibrary.links.notAllowedError';
  }
  return 'exerciseLibrary.links.saveError';
}
